import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { repairFollowupEventTypeEnum, repairFollowupStatusEnum } from './enums';
import { users } from './users';

// ============================================
// Repair Follow-Up Tracking System
// ============================================

// Watched statuses that trigger the 30-day follow-up reminder
export const REPAIR_FOLLOWUP_WATCHED_STATUSES = [
  "contractor_in_production_admin",
  "contractor_needs_supplement", 
  "need_depreciation_released_desk"
] as const;


// Main watch record - tracks claims in repair-related statuses
export const repairFollowupWatch = pgTable("repair_followup_watch", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Status tracking
  watchedStatus: varchar("watched_status").notNull(), // The status that triggered the watch
  status: repairFollowupStatusEnum("status").notNull().default("watching"),
  
  // Timing
  statusEnteredAt: timestamp("status_entered_at").notNull(), // When claim entered watched status
  reminderDueAt: timestamp("reminder_due_at").notNull(), // 30 days after status entry
  lastReminderSentAt: timestamp("last_reminder_sent_at"),
  nextReminderAt: timestamp("next_reminder_at"), // For recurring reminders (every 7 days)
  reminderCount: integer("reminder_count").default(0),
  
  // Acknowledgment
  acknowledgedAt: timestamp("acknowledged_at"),
  acknowledgedBy: varchar("acknowledged_by").references(() => users.id),
  acknowledgedSource: varchar("acknowledged_source"), // "manual", "email_sent", "auto"
  acknowledgedNotes: text("acknowledged_notes"),
  contactedParty: varchar("contacted_party"), // "contractor", "policyholder", "both"
  
  // Email tracking
  relatedEmailId: varchar("related_email_id"), // Gmail message ID that triggered auto-ack
  relatedThreadId: varchar("related_thread_id"), // Gmail thread ID for replies
  
  // Work completion tracking
  workCompleteDetectedAt: timestamp("work_complete_detected_at"),
  depreciationAlertCreatedAt: timestamp("depreciation_alert_created_at"),
  depreciationAlertTaskId: uuid("depreciation_alert_task_id"),
  
  // Assigned user (typically the desk adjuster)
  assignedUserId: varchar("assigned_user_id").references(() => users.id),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_repair_followup_watch_claim_id").on(table.claimId),
  index("idx_repair_followup_watch_status").on(table.status),
  index("idx_repair_followup_watch_reminder_due").on(table.reminderDueAt),
  index("idx_repair_followup_watch_next_reminder").on(table.nextReminderAt),
  index("idx_repair_followup_watch_assigned_user").on(table.assignedUserId),
]);


// Event log for full audit trail
export const repairFollowupEvents = pgTable("repair_followup_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  watchId: uuid("watch_id").references(() => repairFollowupWatch.id, { onDelete: "cascade" }).notNull(),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  eventType: repairFollowupEventTypeEnum("event_type").notNull(),
  eventDate: timestamp("event_date").defaultNow().notNull(),
  
  // Event details
  details: jsonb("details"), // Flexible storage for event-specific data
  notes: text("notes"),
  
  // Actor
  performedBy: varchar("performed_by").references(() => users.id),
  performedBySystem: boolean("performed_by_system").default(false),
  
  // Related entities
  relatedEmailId: varchar("related_email_id"),
  relatedTaskId: uuid("related_task_id"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_repair_followup_events_watch_id").on(table.watchId),
  index("idx_repair_followup_events_claim_id").on(table.claimId),
  index("idx_repair_followup_events_type").on(table.eventType),
  index("idx_repair_followup_events_date").on(table.eventDate),
]);
